import { useEffect, useState } from "react";
import { X, Save, Camera, Loader2, User } from "lucide-react";
import axios from "../../../config/axios";
import "toastr/build/toastr.min.css";
import toastr from "toastr";
import { AnimatePresence, motion } from "framer-motion";
import LoadingSpinner from "../../../components/ui/loading-spinner";
import InputForm from "../../../components/ui/Input-form";
import SelectForm from "../../../components/ui/SelectForm";
import T from "../../../components/translate/AutoTranslator";

export default function ApprenantEditModal({
  onClose,
  data,
  loading,
  updateLearner,
  updatePhotoLearner,
}) {
  const [formData, setFormData] = useState({
    emp_name: "",
    emp_firstname: "",
    emp_matricule: "",
    emp_email: "",
    emp_phone: "",
    emp_lot: "",
    emp_qrt: "",
    emp_cp: "",
    idVille: "",
    idEntreprise: "",
    emp_photo: "",
    idEmploye: "",
  });

  useEffect(() => {
    if (data?.learner) {
      setFormData({
        emp_name: data.learner.emp_name || "",
        emp_firstname: data.learner.emp_firstname || "",
        emp_matricule: data.learner.emp_matricule || "",
        emp_email: data.learner.emp_email || "",
        emp_phone: data.learner.emp_phone || "",
        emp_lot: data.learner.emp_lot || "",
        emp_qrt: data.learner.emp_qrt || "",
        emp_cp: data.learner.emp_cp || "",
        idVille: data.learner.idVille || "",
        idEntreprise: data.learner.idEtp || "",
        emp_photo: data.learner.emp_photo || "",
        idEmploye: data.learner.idEmploye || "",
      });
    }
  }, [data]);

  const [submitting, setSubmitting] = useState(false);
  const [photo, setPhoto] = useState(null);
  const imgUrl = process.env.REACT_APP_URL_IMAGE + "/img/employes/";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(URL.createObjectURL(file));
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({ ...prev, emp_photo_base64: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdatePhoto = async (idApprenant) => {
    if (!formData.emp_photo_base64) return;
    try {
      await updatePhotoLearner(formData.emp_photo_base64, idApprenant);
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const handleUpdateInfo = async (idApprenant) => {
    try {
      const res = await updateLearner(formData, idApprenant);

      return res;
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (formData.emp_photo_base64) {
        await handleUpdatePhoto(formData.idEmploye);
      }

      const res = await handleUpdateInfo(formData.idEmploye);
      if ((res && res.status === 200) || res.status === 500) {
        toastr.success("Modifié avec succès");
        onClose();
      } else {
        toastr.error(res.message || "Erreur lors de la modification");
      }
    } catch (err) {
      console.error(err);
      toastr.error("Une erreur est survenue lors de la modification");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[700] overflow-y-auto"
      >
        <div className="flex min-h-screen items-center justify-center p-4 text-center">
          <motion.div
            className="fixed inset-0 bg-black/20"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl sm:my-8 sm:p-6 max-w-2xl w-full mx-auto"
          >
            {loading ? (
              <div className="flex justify-center items-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
              </div>
            ) : data ? (
              <div>
                <div className="flex justify-end">
                  <X
                    onClick={onClose}
                    className="hover:text-gray-300 cursor-pointer"
                  />
                </div>
                <form className="p-4 space-y-3" onSubmit={handleSubmit}>
                  {/* Photo */}
                  <div className="flex flex-col items-center space-y-2">
                    <div className="relative group">
                      {photo ? (
                        <img
                          src={photo}
                          alt="Preview"
                          className="w-28 h-28 rounded-full object-cover border"
                        />
                      ) : formData.emp_photo ? (
                        <img
                          src={`${imgUrl}${formData.emp_photo}`}
                          alt="Employé"
                          className="w-28 h-28 rounded-full object-cover border"
                        />
                      ) : (
                        <User className="w-28 h-28 rounded-full border p-6 text-gray-400" />
                      )}

                      <label
                        htmlFor="photo"
                        className="absolute bottom-0 right-0 bg-blue-600 text-white p-1.5 rounded-full cursor-pointer hover:bg-blue-700"
                      >
                        <Camera size={18} />
                      </label>
                      <input
                        id="photo"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handlePhotoChange}
                      />
                    </div>
                  </div>

                  {/* Matricule */}
                  <div>
                    <InputForm
                      label="Matricule"
                      type="text"
                      name="emp_matricule"
                      value={formData.emp_matricule}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Nom & Prénom */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <InputForm
                        label="Nom"
                        type="text"
                        name="emp_name"
                        value={formData.emp_name}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <InputForm
                        label="Prénom"
                        type="text"
                        name="emp_firstname"
                        value={formData.emp_firstname}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Téléphone */}
                  <div>
                    <InputForm
                      label="Téléphone"
                      type="text"
                      name="emp_phone"
                      value={formData.emp_phone}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <InputForm
                      label="Adresse e-mail"
                      type="email"
                      name="emp_email"
                      value={formData.emp_email}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Ville */}
                  <div>
                    <SelectForm
                      label="Ville"
                      data={data.villes || []}
                      name="idVille"
                      value={formData.idVille}
                      onChange={handleChange}
                      optionValue="idVille"
                      optionLabel="ville"
                    />
                  </div>

                  {/* Entreprise */}
                  <div>
                    <SelectForm
                      label="Entreprise"
                      data={data.etps || []}
                      name="idEntreprise"
                      value={formData.idEntreprise}
                      onChange={handleChange}
                      optionValue="idEtp"
                      optionLabel="etp_name"
                    />
                  </div>

                  {/* Footer */}
                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-200 disabled:opacity-70 flex items-center disabled:cursor-not-allowed"
                      disabled={submitting}
                    >
                      <T>Annuler</T>
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-70 flex items-center disabled:cursor-not-allowed"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <Loader2 className="animate-spin" size={18} />
                      ) : (
                        <Save size={18} />
                      )}
                      <span className="ml-2">
                        {submitting ? (
                          <T>Enregistrement...</T>
                        ) : (
                          <T>Enregistrer</T>
                        )}
                      </span>
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="text-center py-10">
                <p>Erreur lors du chargement des données</p>
              </div>
            )}
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
